import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, ButtonBase, Stack, Typography, useTheme } from "@mui/material";
import { alpha, darken, lighten } from "@mui/material/styles";
import TuneRounded from "@mui/icons-material/TuneRounded";
import EventAvailableRounded from "@mui/icons-material/EventAvailableRounded";
import EventRepeatRounded from "@mui/icons-material/EventRepeatRounded";
import EventBusyRounded from "@mui/icons-material/EventBusyRounded";
import NotificationsActiveRounded from "@mui/icons-material/NotificationsActiveRounded";
import TaskAltRounded from "@mui/icons-material/TaskAltRounded";
import Brand from "../../../api/Brand";
import { useCookies } from "react-cookie";
import BottomButton from "../../BottomButton";
import ProgressHeader from "../../FirstSetup/ProgressHeader";
import { isFirstSetupProps } from "../../../interfaces/isFirstSetupProps";
import HoursBlock from "../../HoursBlock";
import ToggleButton from "../../ToggleButton";
import { useNavigate } from "react-router-dom";
import { FullScreenLoader } from "../../FullScreenLoader";
import ColoredIcon from "../../ColoredIcon";
import BackIcon from "../../../img/Arrow_Left.svg";
import { FONTS, CARD_STYLES } from "../../../constants/styles";

const NOISE_TEXTURE = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

const AutomationEdit: React.FC<isFirstSetupProps> = ({
    isFirstSetup = false,
}) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const navigate = useNavigate();

    const [brand, setBrand] = useState<Brand | undefined>();
    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);

    const [allowOnlineBooking, setAllowOnlineBooking] = useState<boolean>(true);
    const [allowReschedule, setAllowReschedule] = useState<boolean>(true);
    const [allowCancel, setAllowCancel] = useState<boolean>(true);
    const [turnOnReminder, setTurnOnReminder] = useState<boolean>(true);
    const [requestConfirmation, setRequestConfirmation] =
        useState<boolean>(true);

    const [rescheduleHours, setRescheduleHours] = useState<number>(24);
    const [cancelHours, setCancelHours] = useState<number>(48);
    const [reminderHours, setReminderHours] = useState<number>(25);
    const [confirmationHours, setConfirmationHours] = useState<number>(24);

    const [cookies] = useCookies(["uuid", "brand_id"]);
    const { uuid, brand_id } = cookies;

    const cancelationInputRef = useRef<HTMLInputElement>(null);
    const reminderInputRef = useRef<HTMLInputElement>(null);
    const confirmationInputRef = useRef<HTMLInputElement>(null);

    // Scroll tracking
    const scrollRef = useRef<HTMLDivElement>(null);
    const heroRef = useRef<HTMLDivElement>(null);
    const [scrolledPastHero, setScrolledPastHero] = useState(false);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        const heroEl = heroRef.current;
        if (!el || !heroEl) return;
        setScrolledPastHero(el.scrollTop > heroEl.offsetHeight - 60);
    }, []);

    const iconBoxSx = {
        width: 32,
        height: 32,
        borderRadius: "10px",
        bgcolor: alpha(primary, 0.1),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "0 0 auto",
    };

    useEffect(() => {
        if (!uuid || !brand_id) {
            window.location.href = "/";
        }
        Brand.info(uuid, brand_id)
            .then((b) => {
                if (!b) {
                    setLoadingError("Failed to load automation settings");
                    return;
                }
                setBrand(b);
                setAllowOnlineBooking(b.booking ?? true);
                setAllowReschedule(b.reschedule ?? true);
                setRescheduleHours(b.appointmentReschedule ?? 2);
                setAllowCancel(b.cancellation ?? true);
                setCancelHours(b.appointmentCancellation ?? 4);
                setTurnOnReminder(b.reminder ?? true);
                setReminderHours(b.appointmentReminder ?? 4);
                setRequestConfirmation(b.confirmation ?? true);
                setConfirmationHours(b.appointmentConfirmation ?? 4);
            })
            .catch((err) => {
                setLoadingError(err);
            })
            .finally(() => setLoading(false));
    }, [uuid, brand_id]);

    const onNext = useCallback(() => {
        if (!uuid || !brand_id || !brand) return;
        Brand.updateAutomationSettings(
            uuid,
            brand_id,
            allowOnlineBooking,
            allowReschedule,
            rescheduleHours,
            allowCancel,
            cancelHours,
            turnOnReminder,
            reminderHours,
            requestConfirmation,
            confirmationHours
        ).then(() => {
            navigate(isFirstSetup ? "/setup/profile" : "/");
        });
    }, [
        uuid,
        brand_id,
        allowCancel,
        allowOnlineBooking,
        allowReschedule,
        brand,
        cancelHours,
        confirmationHours,
        reminderHours,
        requestConfirmation,
        rescheduleHours,
        turnOnReminder,
        isFirstSetup,
        navigate,
    ]);

    const onBack = useCallback(() => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate("/");
        }
    }, [navigate]);

    if (loading) return <FullScreenLoader />;
    if (loadingError) return <Typography>{loadingError}</Typography>;

    const dividerSx = { borderTop: `1px solid ${alpha(primary, 0.08)}`, mx: -2 };

    return (
        <>
            {isFirstSetup && (
                <ProgressHeader step={5} title="Notifications" />
            )}

            <Stack
                ref={scrollRef}
                onScroll={handleScroll}
                flex={1}
                sx={{ overflowY: "auto", overflowX: "hidden", bgcolor: "white" }}
            >
                {/* ── Hero (compact) ── */}
                <Stack
                    ref={heroRef}
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        width: "100%",
                        minHeight: 120,
                        position: "relative",
                        zIndex: 0,
                        background: `linear-gradient(180deg, ${lighten(primary, 0.08)} 0%, ${darken(primary, 0.08)} 100%)`,
                        transition: "background 0.3s ease",
                        pt: "40px",
                        pb: 4,
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            opacity: 0.04,
                            backgroundImage: NOISE_TEXTURE,
                            backgroundRepeat: "repeat",
                            backgroundSize: "128px 128px",
                            pointerEvents: "none",
                        },
                    }}
                >
                    {/* Back arrow — hidden in first setup */}
                    {!isFirstSetup && (
                        <ButtonBase
                            onClick={onBack}
                            sx={{
                                position: "absolute", top: 12, left: 10, zIndex: 2,
                                width: 36, height: 36, borderRadius: "50%",
                                bgcolor: "rgba(60,60,60,0.35)",
                                backdropFilter: "blur(16px)",
                                WebkitBackdropFilter: "blur(16px)",
                                border: "1px solid rgba(255,255,255,0.2)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}
                        >
                            <ColoredIcon icon={BackIcon} color="white" size="28px" />
                        </ButtonBase>
                    )}

                    {/* Frosted circle + icon */}
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: "50%",
                            bgcolor: "rgba(60,60,60,0.3)",
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                            border: "1px solid rgba(255,255,255,0.15)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 1,
                        }}
                    >
                        <TuneRounded sx={{ fontSize: 32, color: "white" }} />
                    </Box>

                    {/* Frosted pill */}
                    <Box
                        sx={{
                            bgcolor: "rgba(60,60,60,0.35)",
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            borderRadius: "16px",
                            px: 1.5,
                            py: 0.5,
                        }}
                    >
                        <Typography fontSize="12px" fontWeight={500} color="white">
                            Automation Settings
                        </Typography>
                    </Box>
                </Stack>

                {/* ── Sticky Toolbar ── */}
                <Box
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 4,
                        bgcolor: "white",
                        borderTopLeftRadius: "24px",
                        borderTopRightRadius: "24px",
                        mt: "-24px",
                        pt: 1.5,
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        sx={{
                            maxHeight: scrolledPastHero ? 52 : 0,
                            opacity: scrolledPastHero ? 1 : 0,
                            overflow: "hidden",
                            transition: "max-height 0.25s ease, opacity 0.2s ease",
                            px: 1,
                            borderBottom: scrolledPastHero
                                ? "1px solid rgba(0,0,0,0.06)"
                                : "none",
                        }}
                    >
                        {!isFirstSetup && (
                            <ButtonBase onClick={onBack} sx={{ p: 0.5 }}>
                                <ColoredIcon
                                    icon={BackIcon}
                                    color={primary}
                                    size="28px"
                                />
                            </ButtonBase>
                        )}
                        <Typography
                            fontFamily={FONTS.display}
                            fontSize="16px"
                            fontWeight={600}
                            ml={0.5}
                            noWrap
                            sx={{
                                flex: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            Automation Settings
                        </Typography>
                    </Stack>
                </Box>

                {/* ── Bottom Sheet Content ── */}
                <Stack
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        bgcolor: "white",
                        boxSizing: "border-box",
                        px: 2,
                        pt: 0.5,
                        pb: "calc(4vh + 90px)",
                        "@keyframes fadeSlideIn": {
                            from: { opacity: 0, transform: "translateY(12px)" },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                        animation: "fadeSlideIn 0.3s ease-out",
                    }}
                    spacing={2}
                >
                    {/* ── BOOKING section ── */}
                    <Typography
                        fontSize="11px"
                        fontWeight={600}
                        color="text.secondary"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                        mb={-1}
                    >
                        Booking
                    </Typography>
                    <Stack sx={CARD_STYLES} spacing={2}>
                        {/* Allow online booking */}
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Box sx={iconBoxSx}>
                                <EventAvailableRounded
                                    sx={{ fontSize: 18, color: primary }}
                                />
                            </Box>
                            <Typography
                                fontWeight={400}
                                fontSize="12px"
                                lineHeight="18px"
                                color="text.secondary"
                                flex={1}
                            >
                                Allow online booking
                            </Typography>
                            <ToggleButton
                                value={allowOnlineBooking}
                                onClick={setAllowOnlineBooking}
                            />
                        </Stack>

                        <Box sx={dividerSx} />

                        {/* Allow reschedule */}
                        <HoursBlock
                            name="Allow reschedule"
                            icon={
                                <EventRepeatRounded
                                    sx={{ fontSize: 18, color: primary }}
                                />
                            }
                            allow={allowReschedule}
                            setAllow={setAllowReschedule}
                            prefix="Not less than"
                            hours={rescheduleHours}
                            setHours={setRescheduleHours}
                            nextRef={cancelationInputRef}
                        />

                        <Box sx={dividerSx} />

                        {/* Allow cancel */}
                        <HoursBlock
                            name="Allow cancel"
                            icon={
                                <EventBusyRounded
                                    sx={{ fontSize: 18, color: primary }}
                                />
                            }
                            allow={allowCancel}
                            setAllow={setAllowCancel}
                            prefix="Not less than"
                            hours={cancelHours}
                            setHours={setCancelHours}
                            ref={cancelationInputRef}
                            nextRef={reminderInputRef}
                        />
                    </Stack>

                    {/* ── NOTIFICATIONS section ── */}
                    <Typography
                        fontSize="11px"
                        fontWeight={600}
                        color="text.secondary"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                        mb={-1}
                    >
                        Notifications
                    </Typography>
                    <Stack sx={CARD_STYLES} spacing={2}>
                        {/* Remind about appointments */}
                        <HoursBlock
                            name="Remind about appointments"
                            icon={
                                <NotificationsActiveRounded
                                    sx={{ fontSize: 18, color: primary }}
                                />
                            }
                            allow={turnOnReminder}
                            setAllow={setTurnOnReminder}
                            prefix="Not less than"
                            hours={reminderHours}
                            setHours={setReminderHours}
                            ref={reminderInputRef}
                            nextRef={confirmationInputRef}
                        />

                        <Box sx={dividerSx} />

                        {/* Request confirmation */}
                        <HoursBlock
                            name="Request confirmation"
                            icon={
                                <TaskAltRounded
                                    sx={{ fontSize: 18, color: primary }}
                                />
                            }
                            allow={requestConfirmation}
                            setAllow={setRequestConfirmation}
                            prefix="Not less than"
                            hours={confirmationHours}
                            setHours={setConfirmationHours}
                            ref={confirmationInputRef}
                        />
                    </Stack>
                </Stack>
            </Stack>

            <BottomButton sticky onClick={onNext}>
                {isFirstSetup ? "Go to profile settings" : "Save Changes"}
            </BottomButton>
        </>
    );
};

export default AutomationEdit;
